// /festive-events/:id — Festive Event Detail (PAGES.md §3.66)
//
// Event detail with menu items, demand forecast, production plan, and order tracking.
// Tabs: Overview / Orders / Production / Forecast.
// RESTAURANT vertical only — MANAGER+ (level 30). Feature flag: ENABLE_FESTIVE_MODULE.
// Traceability: FR-FEST-006 to FR-FEST-010

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  StatCard,
  DataTable,
  Tabs,
  Pagination,
  Modal,
  Input,
  TextArea,
  DatePicker,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { festiveService } from '@/services/festive.service';
import type {
  Festival,
  FestiveOrder,
  ProductionPlan,
  CreateFestivalDto,
} from '@/services/festive.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { GLASS, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { ApiError } from '@/lib/api-client';
import {
  ROLE_LEVELS,
  ORDER_STATUS_BADGE,
  EVENT_STATUS_BADGE,
  PAGE_SIZE,
  formatDate,
} from './constants';
import {
  CalendarIcon, UsersIcon, CurrencyIcon, PackageIcon, EditIcon, MenuIcon, GiftEmptyIcon,
} from './icons';

// ─── Tab keys ───────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'orders', label: 'Orders' },
  { id: 'production', label: 'Production' },
  { id: 'forecast', label: 'Forecast' },
];

// ─── Component ──────────────────────────────────────────────────────────────

function FestiveEventDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canEdit = roleLevel >= 50;
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get('tab') ?? 'overview';
  const setActiveTab = useCallback((tab: string) => {
    setSearchParams({ tab }, { replace: true });
  }, [setSearchParams]);

  // ── State ───────────────────────────────────────────────────────────────
  const [festival, setFestival] = useState<Festival | null>(null);
  const [orders, setOrders] = useState<FestiveOrder[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [production, setProduction] = useState<ProductionPlan | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [ordersPage, setOrdersPage] = useState(1);

  // ── Edit modal state ───────────────────────────────────────────────────
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editMaxOrders, setEditMaxOrders] = useState('');
  const [editAdvancePct, setEditAdvancePct] = useState('');
  const [_editCutoffHours, _setEditCutoffHours] = useState('');
  const [editError, setEditError] = useState<string | null>(null);
  const [isEditSaving, setIsEditSaving] = useState(false);

  // ── Fetch event detail ──────────────────────────────────────────────────
  const fetchEvent = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const event = await festiveService.getFestival(id);
      setFestival(event);
      document.title = `${event.name} — Festive Events — Orio360 Admin`;
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        toast({ title: 'Festival event not found', variant: 'error' });
        navigate('/festive-events');
      } else {
        toast({ title: err instanceof ApiError ? err.message : 'Failed to load event.', variant: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => { fetchEvent(); }, [fetchEvent]);

  // ── Fetch orders for this event ─────────────────────────────────────────
  const fetchOrders = useCallback(async () => {
    if (!id) return;
    try {
      const result = await festiveService.listFestiveOrders({
        festival_id: id,
        page: ordersPage,
        limit: PAGE_SIZE,
      });
      setOrders(result.data);
      setTotalOrders(result.meta.total);
    } catch (err) {
      if (err instanceof ApiError) {
        toast({ title: err.message, variant: 'error' });
      }
    }
  }, [id, ordersPage, toast]);

  useEffect(() => {
    if (activeTab === 'orders') fetchOrders();
  }, [activeTab, fetchOrders]);

  // ── Fetch production plan ───────────────────────────────────────────────
  const fetchProduction = useCallback(async () => {
    if (!id) return;
    try {
      const plan = await festiveService.getProductionPlanning(id);
      setProduction(plan);
    } catch (err) {
      if (err instanceof ApiError) {
        toast({ title: err.message, variant: 'error' });
      }
    }
  }, [id, toast]);

  useEffect(() => {
    if (activeTab === 'production' || activeTab === 'forecast') fetchProduction();
  }, [activeTab, fetchProduction]);

  // ── Edit event handler ─────────────────────────────────────────────────
  const openEditModal = useCallback(() => {
    if (!festival) return;
    setEditName(festival.name);
    setEditDescription(festival.description ?? '');
    setEditStartDate(festival.start_date.split('T')[0]);
    setEditEndDate(festival.end_date.split('T')[0]);
    setEditMaxOrders(festival.total_capacity != null ? String(festival.total_capacity) : '');
    setEditAdvancePct(String(festival.advance_percentage));
    setEditError(null);
    setEditModalOpen(true);
  }, [festival]);

  const handleEditSave = useCallback(async () => {
    if (!id || !editName.trim()) { setEditError('Event name is required'); return; }
    if (!editStartDate || !editEndDate) { setEditError('Both dates required'); return; }
    setIsEditSaving(true);
    setEditError(null);
    try {
      const dto: Partial<CreateFestivalDto> = {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        start_date: editStartDate,
        end_date: editEndDate,
        total_capacity: editMaxOrders ? parseInt(editMaxOrders, 10) : undefined,
        advance_percentage: parseInt(editAdvancePct, 10) || 50,
      };
      await festiveService.updateFestival(id, dto);
      toast({ title: 'Event updated', variant: 'success' });
      setEditModalOpen(false);
      fetchEvent();
    } catch (err) {
      setEditError(err instanceof ApiError ? err.message : 'Failed to update event');
    } finally {
      setIsEditSaving(false);
    }
  }, [id, editName, editDescription, editStartDate, editEndDate, editMaxOrders, editAdvancePct, fetchEvent, toast]);

  // ── Order columns ─────────────────────────────────────────────────────
  const orderColumns: ColumnDef<FestiveOrder>[] = useMemo(() => [
    {
      key: 'customer_name', header: 'Customer',
      render: (_v: unknown, row: FestiveOrder) => (
        <div>
          <span className="font-bold text-neutral-900 dark:text-neutral-100">{row.customer_name}</span>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 font-mono">{row.customer_phone}</p>
        </div>
      ),
    },
    {
      key: 'items', header: 'Items',
      render: (_v: unknown, row: FestiveOrder) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300">
          {row.items.length} item{row.items.length !== 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'total', header: 'Amount', numeric: true,
      render: (_v: unknown, row: FestiveOrder) => (
        <span className="font-mono tabular-nums font-bold text-neutral-900 dark:text-neutral-100">
          {formatPaise(row.total, currency)}
        </span>
      ),
    },
    {
      key: 'delivery_date', header: 'Delivery',
      render: (_v: unknown, row: FestiveOrder) => (
        <span className="text-sm font-mono tabular-nums text-neutral-700 dark:text-neutral-300">
          {formatDate(row.delivery_date)}
          {row.delivery_time_slot && ` · ${row.delivery_time_slot}`}
        </span>
      ),
    },
    {
      key: 'status', header: 'Status',
      render: (_v: unknown, row: FestiveOrder) => {
        const badge = ORDER_STATUS_BADGE[row.status] ?? ORDER_STATUS_BADGE.BOOKED;
        return <Badge variant={badge.variant} size="sm">{badge.label}</Badge>;
      },
    },
  ], [currency]);

  if (isLoading || !festival) {
    return (
      <PageContainer
        title="Loading..."
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Festive Events', href: '/festive-events' },
          { label: '...' },
        ]}
      >
        <div className="animate-fade-in space-y-6">
          <div className={cn(GLASS, 'p-6')}>
            <Skeleton variant="text" width="40%" height={28} />
            <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => <StatCard key={i} title="" value="" loading />)}
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  const statusBadge = EVENT_STATUS_BADGE[festival.status] ?? EVENT_STATUS_BADGE.UPCOMING;
  const ordersTotalPages = Math.ceil(totalOrders / PAGE_SIZE);

  return (
    <PageContainer
      title={festival.name}
      subtitle={`${formatDate(festival.start_date)} — ${formatDate(festival.end_date)}`}
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Festive Events', href: '/festive-events' },
        { label: festival.name },
      ]}
      actions={
        <div className="flex items-center gap-3">
          <Badge variant={statusBadge.variant} size="sm">{statusBadge.label}</Badge>
          {canEdit && (
            <>
              <Button variant="secondary" size="sm" onClick={() => navigate(`/festive-events/${id}/menu`)} aria-label="Manage festive menu">
                <MenuIcon /><span className="ml-1.5">Manage Menu</span>
              </Button>
              <Button variant="primary" size="sm" onClick={openEditModal} aria-label="Edit event details">
                <EditIcon /><span className="ml-1.5">Edit Event</span>
              </Button>
            </>
          )}
        </div>
      }
    >
      <div className="animate-fade-in">
        {/* ═══ KPI Stats ═══ */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="animate-slide-up">
            <StatCard
              title="Total Orders"
              value={festival.order_count.toLocaleString('en-IN')}
              icon={<UsersIcon className="h-4 w-4" />}
            />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
            <StatCard
              title="Revenue Forecast"
              value={formatPaise(festival.revenue_forecast, currency)}
              icon={<CurrencyIcon className="h-4 w-4" />}
            />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
            <StatCard
              title="Capacity Used"
              value={festival.total_capacity
                ? `${festival.booked_count}/${festival.total_capacity}`
                : `${festival.booked_count} (unlimited)`}
              icon={<PackageIcon className="h-4 w-4" />}
            />
          </div>
          <div className="animate-slide-up" style={{ animationDelay: '150ms' }}>
            <StatCard
              title="Date Range"
              value={`${Math.ceil((new Date(festival.end_date).getTime() - new Date(festival.start_date).getTime()) / 86400000)} days`}
              icon={<CalendarIcon className="h-4 w-4" />}
            />
          </div>
        </div>

        {/* ═══ Tabs ═══ */}
        <Tabs
          items={TABS}
          activeId={activeTab}
          onChange={setActiveTab}
        />

        <div className="mt-6">
          {/* ── Overview Tab ──────────────────────────────────────────────── */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className={cn(GLASS, 'p-6')}>
                <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-4">
                  Event Details
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Booking Deadline</span>
                      <p className="text-sm font-mono tabular-nums text-neutral-900 dark:text-neutral-100 mt-0.5">
                        {formatDate(festival.booking_deadline)}
                      </p>
                    </div>
                    <div>
                      <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Total Capacity</span>
                      <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 mt-0.5">
                        {festival.total_capacity?.toLocaleString('en-IN') ?? 'Unlimited'}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Advance Payment</span>
                      <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 mt-0.5">
                        {festival.advance_percentage}%
                      </p>
                    </div>
                    {festival.description && (
                      <div>
                        <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Description</span>
                        <p className="text-sm text-neutral-700 dark:text-neutral-300 mt-0.5">
                          {festival.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Orders Tab ────────────────────────────────────────────────── */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              {orders.length === 0 ? (
                <EmptyState
                  variant="page"
                  icon={<GiftEmptyIcon />}
                  title="No orders for this event yet"
                  description="Orders will appear here once customers start booking"
                />
              ) : (
                <>
                  <div className={cn(GLASS, 'overflow-hidden hidden sm:block')}>
                    <div className="px-5 py-4">
                      <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                        Orders ({totalOrders})
                      </h3>
                    </div>
                    <DataTable<FestiveOrder> columns={orderColumns} data={orders} />
                  </div>

                  {/* Mobile cards */}
                  <div className="block sm:hidden space-y-3">
                    {orders.map((order) => {
                      const badge = ORDER_STATUS_BADGE[order.status] ?? ORDER_STATUS_BADGE.BOOKED;
                      return (
                        <div key={order.id} className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-bold text-neutral-900 dark:text-neutral-100 truncate">{order.customer_name}</p>
                              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 font-mono tabular-nums">
                                {formatDate(order.delivery_date)} · {order.items.length} items
                              </p>
                            </div>
                            <Badge variant={badge.variant} size="sm">{badge.label}</Badge>
                          </div>
                          <div className="flex items-center justify-between mt-3">
                            <span className="text-sm font-bold font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
                              {formatPaise(order.total, currency)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {ordersTotalPages > 1 && (
                    <Pagination
                      currentPage={ordersPage}
                      totalPages={ordersTotalPages}
                      totalItems={totalOrders}
                      pageSize={PAGE_SIZE}
                      onPageChange={setOrdersPage}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* ── Production Tab ────────────────────────────────────────────── */}
          {activeTab === 'production' && (
            <div className="space-y-6">
              {!production || production.items.length === 0 ? (
                <EmptyState
                  variant="page"
                  icon={<PackageIcon className="h-14 w-14" />}
                  title="No production plan"
                  description="Production planning data will appear once orders are confirmed"
                />
              ) : (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <StatCard title="Total Items" value={production.total_items.toLocaleString('en-IN')} icon={<PackageIcon className="h-4 w-4" />} />
                    <StatCard title="Ingredients" value={production.total_ingredients.toLocaleString('en-IN')} icon={<MenuIcon className="h-4 w-4" />} />
                    <StatCard title="Procurement Cost" value={formatPaise(production.procurement_cost_estimate, currency)} icon={<CurrencyIcon className="h-4 w-4" />} />
                  </div>
                  {production.items.map((item) => (
                    <div key={item.product_name} className={cn(GLASS, 'p-4')}>
                      <h4 className="font-bold text-neutral-900 dark:text-neutral-100 mb-3">{item.product_name}</h4>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2 font-mono tabular-nums">
                        Total ordered: {item.total_ordered.toLocaleString('en-IN')}
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" role="table">
                          <thead>
                            <tr className="text-xs text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                              <th scope="col" className="text-left py-2 pr-4">Ingredient</th>
                              <th scope="col" className="text-right py-2 pr-4">Required</th>
                              <th scope="col" className="text-right py-2 pr-4">In Stock</th>
                              <th scope="col" className="text-right py-2">Shortfall</th>
                            </tr>
                          </thead>
                          <tbody>
                            {item.ingredients_needed.map((ing) => (
                              <tr key={ing.name} className="even:bg-neutral-50 dark:even:bg-neutral-800/50">
                                <td className="py-1.5 pr-4 text-neutral-900 dark:text-neutral-100">{ing.name}</td>
                                <td className="py-1.5 pr-4 text-right font-mono tabular-nums">{ing.quantity} {ing.unit}</td>
                                <td className="py-1.5 pr-4 text-right font-mono tabular-nums">{ing.in_stock}</td>
                                <td className="py-1.5 text-right font-mono tabular-nums">
                                  {ing.shortfall > 0 ? (
                                    <Badge variant="danger" size="sm">-{ing.shortfall}</Badge>
                                  ) : (
                                    <Badge variant="success" size="sm">OK</Badge>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* ── Forecast Tab (inline summary + link) ─────────────────────── */}
          {activeTab === 'forecast' && (
            <div className="space-y-6">
              {production ? (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="animate-slide-up">
                      <StatCard title="Forecasted Items" value={production.total_items.toLocaleString('en-IN')} icon={<PackageIcon className="h-4 w-4" />} />
                    </div>
                    <div className="animate-slide-up" style={{ animationDelay: '50ms' }}>
                      <StatCard title="Ingredients Required" value={production.total_ingredients.toLocaleString('en-IN')} icon={<MenuIcon className="h-4 w-4" />} />
                    </div>
                    <div className="animate-slide-up" style={{ animationDelay: '100ms' }}>
                      <StatCard title="Estimated Cost" value={formatPaise(production.procurement_cost_estimate, currency)} icon={<CurrencyIcon className="h-4 w-4" />} />
                    </div>
                  </div>
                  <div className={cn(GLASS, 'p-4')}>
                    <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider mb-3">
                      Top Products by Demand
                    </h3>
                    <div className="space-y-2">
                      {production.items.slice(0, 5).map((item) => (
                        <div key={item.product_name} className="flex items-center justify-between py-1.5">
                          <span className="text-sm text-neutral-900 dark:text-neutral-100">{item.product_name}</span>
                          <span className="text-sm font-mono tabular-nums font-bold text-violet-700 dark:text-violet-400">
                            {item.total_ordered.toLocaleString('en-IN')} units
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className={cn(GLASS, 'p-6 text-center')}>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    No forecast data available yet. Orders need to be confirmed first.
                  </p>
                </div>
              )}
              <div className={cn(GLASS, 'p-4')}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Detailed Demand Forecast</p>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                      View comprehensive forecast with historical comparisons
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => navigate('/reports/demand-forecast')}
                    aria-label="View demand forecast report"
                  >
                    View Forecast
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ Edit Event Modal ═══ */}
      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit Festive Event" variant="wide">
        <div className="space-y-4 p-1 max-h-[70vh] overflow-y-auto">
          {editError && (
            <div className="text-xs text-danger-600 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 rounded-lg" role="alert">
              {editError}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Event Name *</label>
            <Input
              value={editName}
              onChange={(val) => { setEditName(val); setEditError(null); }}
              placeholder="e.g. Diwali 2026"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Start Date *</label>
              <DatePicker
                value={editStartDate ? new Date(editStartDate + 'T00:00:00') : undefined}
                onChange={(d: Date) => { setEditStartDate(d.toLocaleDateString('en-CA')); setEditError(null); }}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">End Date *</label>
              <DatePicker
                value={editEndDate ? new Date(editEndDate + 'T00:00:00') : undefined}
                onChange={(d: Date) => { setEditEndDate(d.toLocaleDateString('en-CA')); setEditError(null); }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Max Total Orders</label>
              <Input type="number" value={editMaxOrders} onChange={(val) => setEditMaxOrders(val)} placeholder="Unlimited" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Advance Payment %</label>
              <Input type="number" value={editAdvancePct} onChange={(val) => setEditAdvancePct(val)} placeholder="50" />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Description</label>
            <TextArea value={editDescription} onChange={(val) => setEditDescription(val)} placeholder="Description..." rows={3} />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setEditModalOpen(false)} disabled={isEditSaving}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleEditSave} disabled={isEditSaving}>
              {isEditSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

export function FestiveEventDetailPage() {
  return (
    <ErrorBoundary>
      <FestiveEventDetailContent />
    </ErrorBoundary>
  );
}
